import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { Badge, Button, EmptyState, Section, Spinner } from '@zt/ui';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { PageDesignsView } from '../lib/types';
import { StorePicker, useStoreChoice } from './payments';
import { openInNewTab } from '../lib/open-tab';

type Experience = PageDesignsView['experiences'][number]['key'];
type Variant = 'a' | 'b' | 'c';
const VARIANTS: Variant[] = ['a', 'b', 'c'];

/**
 * Önizleme görselleri: Playwright ile gerçek vitrin sayfasından alınmış ekran görüntüleri (çizim değil; uçtan uca
 * test mağazası, `apps/admin/e2e/page-designs.spec.ts`). Görsel yoksa yerine açıklama gösterilir.
 */
function Shot({
  experience,
  variant,
  device,
  alt,
}: {
  experience: Experience;
  variant: Variant;
  device: 'desktop' | 'mobile';
  alt: string;
}) {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <img
      src={`/page-designs/${experience}-${variant}-${device}.jpg`}
      alt={alt}
      loading="lazy"
      decoding="async"
      onError={() => setFailed(true)}
      className={
        device === 'desktop'
          ? 'block aspect-[16/10] w-full rounded-md border border-line object-cover object-top'
          : 'absolute bottom-2 right-2 aspect-[9/16] w-[26%] rounded-md border border-line object-cover object-top shadow-lg'
      }
    />
  );
}

/**
 * Sayfa tasarımları (F7-34, ADR-144): vitrinin üst menüsü ve Makaleler sayfası için üçer tasarım seçeneği. Her kartta
 * gerçek sayfadan alınmış mobil ve masaüstü görüntü, seçeneğin karakteri, "Önizle" (yalnızca sizin tarayıcınızda,
 * yayına almadan) ve "Yayına al" (anında, iki adımlı doğrulamayla). Seçenekler aynı veriyi ve adresleri kullanır.
 */
export function PageDesignsPage() {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const canPreview = can('theme.edit');
  const canPublish = can('theme.publish');
  const { stores, loading, storeId, setStoreId } = useStoreChoice();
  const q = useQuery({
    queryKey: ['page-designs', storeId],
    queryFn: () => api.get<PageDesignsView>(`/v1/admin/stores/${storeId}/page-designs`),
    enabled: !!storeId,
  });
  const preview = useApiMutation(
    (v: { experience: Experience; variant: Variant }) =>
      api.post<{ url: string }>(`/v1/admin/stores/${storeId}/page-designs/preview-link`, v),
    {
      onSuccess: (r) => {
        if (!openInNewTab(r.url)) toast.message(t('admin.design.previewBlocked'), { description: r.url });
      },
    },
  );
  const publish = useApiMutation(
    async (v: { experience: Experience; variant: Variant }) => {
      await api.put<PageDesignsView>(`/v1/admin/stores/${storeId}/page-designs`, v);
      return v;
    },
    {
      invalidate: [['page-designs', storeId]],
      success: (v) =>
        t('admin.pageDesigns.published', {
          experience: t(`admin.pageDesigns.experiences.${v.experience}.title`),
          variant: v.variant.toUpperCase(),
        }),
    },
  );

  if (loading) return <Spinner label={t('common.loading')} />;
  return (
    <PageFrame helpKey="page.page_designs" wide>
      {!stores.length ? (
        <EmptyState title={t('admin.payments.noStore')} description={t('admin.payments.noStoreLead')} />
      ) : !q.data ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <>
          <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
          {q.data.experiences.map((exp) => (
            <Section
              key={exp.key}
              title={t(`admin.pageDesigns.experiences.${exp.key}.title`)}
              description={t(`admin.pageDesigns.experiences.${exp.key}.lead`)}
              action={<HelpTip k={`page_designs.${exp.key}`} />}
            >
              <ul
                className="grid gap-4 md:grid-cols-3"
                aria-label={t(`admin.pageDesigns.experiences.${exp.key}.title`)}
              >
                {VARIANTS.map((v) => {
                  const active = exp.variant === v;
                  const name = t(`admin.pageDesigns.variants.${exp.key}.${v}.name`);
                  const busy =
                    (publish.isPending &&
                      publish.variables?.experience === exp.key &&
                      publish.variables.variant === v) ||
                    (preview.isPending &&
                      preview.variables?.experience === exp.key &&
                      preview.variables.variant === v);
                  return (
                    <li
                      key={v}
                      className={
                        active
                          ? 'flex flex-col gap-3 rounded-lg border-2 border-pine bg-surface p-3'
                          : 'flex flex-col gap-3 rounded-lg border border-line bg-surface p-3'
                      }
                      aria-current={active ? 'true' : undefined}
                    >
                      <div className="relative">
                        <Shot
                          experience={exp.key}
                          variant={v}
                          device="desktop"
                          alt={t('admin.pageDesigns.shotDesktop', { name })}
                        />
                        <Shot
                          experience={exp.key}
                          variant={v}
                          device="mobile"
                          alt={t('admin.pageDesigns.shotMobile', { name })}
                        />
                      </div>
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-cond text-lg font-semibold">
                          {t('admin.pageDesigns.option', { letter: v.toUpperCase(), name })}
                        </h3>
                        {active && <Badge tone="pine">{t('admin.pageDesigns.live')}</Badge>}
                      </div>
                      <p className="text-sm text-ink-2">
                        {t(`admin.pageDesigns.variants.${exp.key}.${v}.character`)}
                      </p>
                      <div className="mt-auto flex flex-wrap items-center gap-2">
                        {canPreview && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => preview.mutate({ experience: exp.key, variant: v })}
                              disabled={busy}
                              aria-label={t('admin.pageDesigns.previewOf', { name })}
                            >
                              <ExternalLink className="h-4 w-4" aria-hidden />
                              {t('admin.pageDesigns.preview')}
                            </Button>
                            <HelpTip k="page_designs.preview" />
                          </>
                        )}
                        {canPublish && !active && (
                          <>
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() => publish.mutate({ experience: exp.key, variant: v })}
                              disabled={busy}
                              aria-label={t('admin.pageDesigns.publishOf', { name })}
                            >
                              {t('admin.pageDesigns.publish')}
                            </Button>
                            <HelpTip k="page_designs.publish" />
                          </>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Section>
          ))}
          <Section
            title={t('admin.pageDesigns.history')}
            description={t('admin.pageDesigns.historyLead')}
            action={<HelpTip k="page_designs.history" />}
          >
            {!q.data.history.length ? (
              <p className="text-sm text-ink-3">{t('admin.pageDesigns.noHistory')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" aria-label={t('admin.pageDesigns.history')}>
                  <thead>
                    <tr className="border-b border-line text-start text-ink-3">
                      <th className="py-2 pe-4 text-start font-medium">{t('admin.pageDesigns.colWhen')}</th>
                      <th className="py-2 pe-4 text-start font-medium">{t('admin.pageDesigns.colWho')}</th>
                      <th className="py-2 pe-4 text-start font-medium">{t('admin.pageDesigns.colWhat')}</th>
                      <th className="py-2 text-start font-medium">{t('admin.pageDesigns.colChange')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {q.data.history.map((h, i) => (
                      <tr key={i} className="border-b border-line last:border-0">
                        <td className="py-2 pe-4">{formatDate(h.at)}</td>
                        <td className="py-2 pe-4">{h.by ?? '—'}</td>
                        <td className="py-2 pe-4">
                          {t(`admin.pageDesigns.experiences.${h.experience}.title`)}
                        </td>
                        <td className="py-2">
                          {t('admin.pageDesigns.change', {
                            from: h.from.toUpperCase(),
                            to: h.to.toUpperCase(),
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </>
      )}
    </PageFrame>
  );
}
